import React, { useState } from 'react';
import { MapPin, Phone, Mail, Linkedin, Twitter, Send, Loader2 } from 'lucide-react';
import { contactInfo } from '../data/mock';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        toast.success(response.data.message, {
          duration: 5000,
          style: {
            background: '#000000',
            color: '#FFFFFF',
            border: '1px solid #00FFD1'
          }
        });
        
        // Reset form on success
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      
      let errorMessage = 'An error occurred while sending your message. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      toast.error(errorMessage, {
        duration: 6000,
        style: {
          background: '#000000',
          color: '#FFFFFF',
          border: '1px solid #ff4444'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="dark-container py-20">
      <div className="dark-content-container">
        <div className="text-center mb-16">
          <h2 className="display-large mb-6">Get In Touch</h2>
          <p className="body-large max-w-4xl mx-auto">
            Ready to transform your business with cutting-edge technology? Let's discuss your requirements.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="heading-1 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ 
                    background: 'rgba(0, 255, 209, 0.2)' 
                  }}>
                    <MapPin size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <div>
                    <h4 className="heading-3 mb-2">Address</h4>
                    <p className="body-medium text-gray-300">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ 
                    background: 'rgba(0, 255, 209, 0.2)' 
                  }}>
                    <Phone size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <div>
                    <h4 className="heading-3 mb-2">Phone</h4>
                    <p className="body-medium text-gray-300">{contactInfo.phone}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ 
                    background: 'rgba(0, 255, 209, 0.2)' 
                  }}>
                    <Mail size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <div>
                    <h4 className="heading-3 mb-2">Email</h4>
                    <p className="body-medium text-gray-300">{contactInfo.email}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="heading-2 mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <button className="p-3 rounded-lg dark-transition" style={{ 
                  background: 'rgba(0, 255, 209, 0.1)' 
                }}>
                  <Linkedin size={24} style={{ color: '#00FFD1' }} />
                </button>
                <button className="p-3 rounded-lg dark-transition" style={{ 
                  background: 'rgba(0, 255, 209, 0.1)' 
                }}>
                  <Twitter size={24} style={{ color: '#00FFD1' }} />
                </button>
              </div>
            </div>

            <div className="p-6 rounded-lg" style={{ 
              background: 'rgba(0, 255, 209, 0.05)',
              border: '1px solid rgba(0, 255, 209, 0.2)'
            }}>
              <h4 className="heading-2 mb-3">Business Hours</h4>
              <div className="space-y-2">
                <p className="body-medium">Sunday - Thursday: 9:00 AM - 6:00 PM</p>
                <p className="body-medium">Friday - Saturday: Closed</p>
                <p className="body-small text-gray-400 mt-2">Gulf Standard Time (GST)</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block heading-3 mb-3">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-4 rounded-lg bg-transparent border border-gray-600 focus:border-cyan-400 focus:outline-none transition-colors"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#FFFFFF'
                  }}
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block heading-3 mb-3">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full p-4 rounded-lg bg-transparent border border-gray-600 focus:border-cyan-400 focus:outline-none transition-colors"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#FFFFFF'
                  }}
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="company" className="block heading-3 mb-3">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg bg-transparent border border-gray-600 focus:border-cyan-400 focus:outline-none transition-colors"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#FFFFFF'
                  }}
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label htmlFor="message" className="block heading-3 mb-3">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full p-4 rounded-lg bg-transparent border border-gray-600 focus:border-cyan-400 focus:outline-none transition-colors resize-vertical"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#FFFFFF'
                  }}
                  placeholder="Tell us about your project or requirements"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full"
                style={{
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                {isSubmitting ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Send size={20} />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      
      {/* Toast Container */}
      <Toaster 
        position="bottom-right"
        toastOptions={{
          duration: 4000,
        }}
      />
    </section>
  );
};

export default ContactSection;